import { getInterviewSectionTitle } from "@repo/app-types/entities/interviewSection/definition";
import type { TrainingParticipant } from "@repo/app-types/entities/trainingParticipant";
import { type FC, useId, useMemo } from "react";
import { Heading } from "react-aria-components";
import { AudioPlayer } from "../../../components/audioPlayer/audioPlayer.js";
import { AudioPlayerProvider } from "../../../components/audioPlayer/provider.js";
import { useSectionsForJobType } from "../../projects/projectSettings/training/courseHooks.js";
import type { TreeItemKey } from "../../projects/projectSettings/training/courseTreeHelpers.js";
import { useCourseTreeSelection } from "../../projects/projectSettings/training/courseTreeInteractions.js";
import { ChapterAssessmentDetails } from "../../trainingParticipants/chapterAssessmentDetails.js";
import { LessonTranscript } from "../../trainingParticipants/lessonTranscript.js";
import { ParticipantCourseTree } from "../../trainingParticipants/participantCourseTree.js";

export interface TrainingParticipantDetailProps {
  participant: TrainingParticipant;
  initialChapterId: string | undefined;
}

export const TrainingParticipantDetail: FC<TrainingParticipantDetailProps> = (
  props,
) => {
  const {
    sectionsById,
    isLoading: isLoadingSections,
    isError: isErrorSections,
  } = useSectionsForJobType(props.participant.job_type_id);

  const treeLabelId = useId();

  const initialSelectedKey: TreeItemKey | undefined = useMemo(
    () =>
      props.initialChapterId
        ? { type: "chapter", id: props.initialChapterId }
        : undefined,
    [props.initialChapterId],
  );

  const {
    // For the `Tree` component
    selectedKeys,
    onSelectionChange,
    // For us
    selectedChapterNumber,
    selectedChapter,
    selectedLessonNumber,
    selectedSection,
  } = useCourseTreeSelection({
    initialSelectedKey,
    course: props.participant.course,
    sectionsById,
  });

  return (
    <AudioPlayerProvider>
      <div className="grid h-full grid-cols-[min(50%,500px)_minmax(0,1fr)] grid-rows-[minmax(0,1fr)_auto]">
        <div className="grid grid-rows-[auto_minmax(0,1fr)] pl-3">
          <Heading
            id={treeLabelId}
            level={3}
            className="truncate p-3 text-base leading-7 font-semibold text-foreground"
          >
            Learning journey progress
          </Heading>

          <div className="relative overflow-y-auto px-3 pb-3">
            <ParticipantCourseTree
              treeLabelId={treeLabelId}
              sectionsById={sectionsById}
              participant={props.participant}
              selectedKeys={selectedKeys}
              onSelectionChange={onSelectionChange}
            />
          </div>
        </div>

        <div className="grid grid-rows-[auto_minmax(0,1fr)] pr-3">
          <Heading
            level={3}
            className="truncate py-3 pr-6 pl-3 text-base leading-7 font-semibold text-foreground"
          >
            {selectedChapterNumber !== undefined
              ? `Chapter ${selectedChapterNumber}`
              : null}
            {selectedLessonNumber ? `Lesson ${selectedLessonNumber}: ` : null}
            {selectedSection
              ? getInterviewSectionTitle(selectedSection.settings)
              : null}
          </Heading>

          <div className="relative overflow-y-auto px-3 pb-3">
            {selectedChapter ? (
              <ChapterAssessmentDetails
                key={selectedChapter.id}
                projectId={props.participant.project_id}
                participantId={props.participant.id}
                chapterId={selectedChapter.id}
                assessment={
                  props.participant.assessment?.chapters[selectedChapter.id]
                }
                selectedGradingId={null}
              />
            ) : null}

            {selectedSection ? (
              <div className="rounded-lg border border-border-light bg-white p-4">
                <LessonTranscript
                  key={selectedSection.id}
                  title={`Lesson ${selectedLessonNumber}: ${getInterviewSectionTitle(
                    selectedSection.settings,
                  )}`}
                  projectId={props.participant.project_id}
                  participantId={props.participant.id}
                  interviewSectionDefinitionId={selectedSection.id}
                />
              </div>
            ) : null}

            {!selectedChapter && !selectedSection ? (
              <div className="text-sm">
                Select a chapter to view grading details, or a lesson to view
                the transcript.
              </div>
            ) : null}
          </div>
        </div>

        <div className="col-span-2">
          <AudioPlayer />
        </div>
      </div>
    </AudioPlayerProvider>
  );
};
